import {
  useRef, useState, useEffect, useCallback, MouseEvent,
} from 'react';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import {
  useMediaQuery, useTheme,
} from '@mui/material';
import FullCalendar from '@fullcalendar/react';
import listPlugin from '@fullcalendar/list';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import timelinePlugin from '@fullcalendar/timeline';
import interactionPlugin, { EventResizeDoneArg } from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import Stack from '@mui/material/Stack';
import get from 'lodash/get';
import {
  DateSelectArg,
  DateSpanApi,
  DatesSetArg, DayHeaderContentArg,
  EventClickArg,
  EventContentArg,
  EventDropArg, EventMountArg, EventSourceInput,
} from '@fullcalendar/core';
import { useSnackbar } from 'notistack';
import moment from 'moment/moment';
import { useTranslation } from 'react-i18next';
import startCase from 'lodash/startCase';
import Box from '@mui/material/Box';
import _ from 'lodash';
import Divider from '@mui/material/Divider';
import { Iconify, CustomNoRowsOverlay } from '@mgm/common/components';
import { useScreenResize } from '@mgm/common/hooks/useResize';
import { MemoizedBaseAvatar } from '@mgm/common/components/base';
import { getTimezone, convertToCamelCase, isSameOrAfter } from '@mgm/common/utils';
import {
  CalendarStyled,
  CalendarToolbar,
  DayHeaderContent,
  DayOffIndicator,
} from './components';
import { ShiftPopover } from '../Shared/components';
import { useAppDispatch, useAppSelector } from '../../../hooks/useAppStore';
import { useCalendarFilter } from './hooks';
import { useGetScheduleDataAPI } from '../../../hooks/api/useSchedules';
import { useSchedulePermissions } from '../Shared/hooks';
import { schedulesActions } from '../../../store/schedules/slice';
import { getScheduleList } from '../../../store/schedules/selectors';
import { getIsPrintMode } from '../../../store/common/selectors';
import { TScheduleShift } from '../../../types';
import { textColorToDarkestMap, typeColorMap } from './data/calendar';
import { commonActions } from '../../../store/common/slice';

const Calendar = () => {
  const theme = useTheme();
  const calendarRef = useRef<InstanceType<typeof FullCalendar>>(null);
  const { i18n, t } = useTranslation(['schedule', 'common']);
  const { calendarShifts } = useCalendarFilter();
  const { schedulePermissions } = useSchedulePermissions();
  const { pageHeight, resizableDiv, isDesktopSmallScreen } = useScreenResize(100);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [dates, setDates] = useState<any>(null);
  const dispatch = useAppDispatch();
  const schedulesList = useAppSelector(getScheduleList);
  const isPrintMode = useAppSelector(getIsPrintMode);
  const { enqueueSnackbar } = useSnackbar();
  const [view, setView] = useState(isMobile ? 'listWeek' : 'dayGridMonth');
  const [date, setDate] = useState(new Date());

  useEffect(() => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      const newView = isMobile ? 'listWeek' : 'dayGridMonth';
      calendarApi.changeView(newView);
      setView(newView);
    }
  }, [isMobile]);

  const [schedulesCopy, setScheduleCopy] = useState(Object.assign([], schedulesList));

  const { refetch: refetchShifts, isRefetching } = useGetScheduleDataAPI({ dates, isMainSchedulesPage: false });

  const componentRef = useRef<HTMLDivElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (dates) {
      const fetchShifts = async () => {
        await refetchShifts();
      };
      fetchShifts();
    }
  }, [dates, refetchShifts]);

  const handleDropEvent = async (eventInfo: EventDropArg) => {
    const { startStr, endStr, _def } = eventInfo.event;
    const { id } = eventInfo.oldEvent;

    if (eventInfo.event.extendedProps.type) {
      eventInfo.revert();
      enqueueSnackbar(`${t('editingIsNot')}`, {
        variant: 'warning',
      });
      return;
    }

    if (!isSameOrAfter(startStr)) {
      eventInfo.revert();
      enqueueSnackbar(`${t('youCannonDrag')}`, {
        variant: 'warning',
      });
      return;
    }

    const startDate = moment(startStr).format('D/M/YYYY');
    const oldStartDate = moment(eventInfo.oldEvent.startStr).format('D/M/YYYY');

    setScheduleCopy((prevSchedulesCopy) => {
      const newSchedulesCopy = _.cloneDeep(prevSchedulesCopy);

      if (!_.has(newSchedulesCopy, startDate)) {
        _.set(newSchedulesCopy, `${startDate}.schedules`, []);
      }

      _.update(newSchedulesCopy, `${startDate}.schedules`, (schedules = []) => [
        ...schedules,
        {
          ..._def.extendedProps,
          id: Number(id),
          start: startStr,
          end: endStr,
        } as TScheduleShift,
      ]);

      if (_.has(newSchedulesCopy, oldStartDate)) {
        _.update(
          newSchedulesCopy,
          `${oldStartDate}.schedules`,
          (schedules) => _.filter(schedules, (schedule: TScheduleShift) => schedule.id !== Number(id)),
        );
      }

      return newSchedulesCopy;
    });

    dispatch(schedulesActions.setIsDisplayPublishBtn(true));
  };

  const handleSelectEvent = useCallback((arg: EventClickArg) => {
    const { event } = arg;

    setAnchorEl(arg.el);

    const eventData = get(event, '_def.extendedProps');

    const shift = {
      ...eventData,
      start: event.startStr,
      end: event.endStr,
      id: event?.id,
    } as TScheduleShift;

    dispatch(schedulesActions.setSelectedScheduleShift(shift));
  }, [dispatch]);

  const handleResizeEvent = useCallback(async ({ event }: EventResizeDoneArg) => {
    console.log('handleResizeEvent', event);
  }, []);

  const handleDatesSet = useCallback((arg: DatesSetArg) => {
    const start = moment(arg.start);
    const end = moment(arg.end);

    setDates([start.startOf('month').toDate(), end.endOf('month').toDate()]);
  }, []);

  const selectAllow = useCallback((eventInfo: DateSpanApi) => (schedulePermissions.isAllowManage
    ? isSameOrAfter(eventInfo.startStr) : false), [schedulePermissions]);

  const dayHeaderContent = useCallback((info: DayHeaderContentArg) => (
    <Box>
      <DayHeaderContent info={info} />
    </Box>
  ), []);

  const handleEventDidMount = useCallback((info: EventMountArg) => {
    const eventType = info.event.extendedProps.type;
    const eventColor = typeColorMap[eventType] || theme.palette.primary.main;

    // Apply the CSS variable
    info.el.style.setProperty('--fc-event-border-color', eventColor);
  }, [view]);

  const handleClickToday = () => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.today();
      setDate(calendarApi.getDate());
    }
  };

  const handleClickDateNext = () => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.next();
      setDate(calendarApi.getDate());
    }
  };

  const handleClickDatePrev = () => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.prev();

      setDate(calendarApi.getDate());
    }
  };

  const handleSelectRange = (arg: DateSelectArg) => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.unselect();
    }

    dispatch(schedulesActions.setSelectedScheduleShift({} as TScheduleShift));
    dispatch(commonActions.setIsOpenPagesNav(true));
    dispatch(schedulesActions.setSelectedShiftDate(arg.startStr));
  };

  const handleChangeView = (newView: string) => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.changeView(newView);
      setView(newView);
    }
  };

  const onNoteClicked = (event: MouseEvent<HTMLElement>, shift: TScheduleShift) => {
    event.stopPropagation();
    dispatch(schedulesActions.setSelectedScheduleShift(shift));

    dispatch(schedulesActions.setIsOpenShiftNotesNav(true));
  };

  const renderEventContent = useCallback((eventInfo: EventContentArg) => {
    const { event } = eventInfo;

    const shift = get(event, '_def.extendedProps');
    const avatarName = get(event, '_def.extendedProps.name');
    const avatarFile = get(event, '_def.extendedProps.avatarFile');
    const cardType = get(event, '_def.extendedProps.type');
    const shiftNotes = get(event, '_def.extendedProps.notes');
    const status = get(event, '_def.extendedProps.status');
    const textColor = get(event, '_def.ui.textColor');

    const cardText = isMobile ? startCase(avatarName) : event.title;

    return (
      <Stack
        alignItems="center"
        justifyContent="space-between"
        sx={{
          borderRadius: '3px',
          py: 0.3,
        }}
        direction="row"
      >
        <Typography
          variant="caption"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            width: '8rem',
            pl: 1,
            fontWeight: 600,
          }}
          color={isMobile ? typeColorMap[cardType] : textColorToDarkestMap[textColor]}
        >
          {cardType ? t(`${convertToCamelCase(cardType)}`) : cardText}
        </Typography>
        {cardType ? <DayOffIndicator status={status} /> : shiftNotes?.length > 0 && (
          <Box
            sx={{
              cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
              '&:active': {
                backgroundColor: 'transparent',
              },
            }}
            alignItems="center"
            display="flex"
            onClick={(e) => onNoteClicked(e, { ...shift, id: event?.id } as TScheduleShift)}
          >
            <Iconify width={18} icon="proicons:note" />
          </Box>
        )}
        <MemoizedBaseAvatar
          size={{
            width: 22,
            height: 22,
          }}
          fontSize={10}
          sx={{
            position: 'relative',
            zIndex: isMobile ? -1 : 20,
          }}
          avatarName={avatarName}
          avatarFile={avatarFile}
        />
      </Stack>
    );
  }, []);

  return (
    <Card
      sx={{
        ...(isMobile ? {
          backgroundColor: 'unset',
          boxShadow: 'none',
        } : {
          boxShadow: isPrintMode ? 'none' : '',
        }
        ),
      }}
      ref={componentRef}
    >
      <div ref={resizableDiv}>
        {isMobile && <Divider sx={{ borderStyle: 'dashed', mb: 2 }} />}
        <CalendarToolbar
          date={date}
          view={view}
          onNextDate={handleClickDateNext}
          onPrevDate={handleClickDatePrev}
          onToday={handleClickToday}
          onChangeView={handleChangeView}
          printRef={componentRef}
          updatedSchedules={schedulesCopy}
        />
        {isRefetching ? (
          <LinearProgress
            sx={{
              height: 1.2,
            }}
            color="primary"
          />
        ) : (
          <div style={{
            height: 1.2,
          }}
          />
        )}
        <CalendarStyled>
          <FullCalendar
            weekends
            editable
            droppable
            selectable
            locale={i18n.language}
            events={calendarShifts as EventSourceInput}
            ref={calendarRef}
            rerenderDelay={10}
            initialDate={date}
            initialView={view}
            dayMaxEventRows={3}
            eventDisplay="block"
            headerToolbar={false}
            allDayMaintainDuration
            eventResizableFromStart
            eventBorderColor="rgba(168, 85, 247, 0.16)'"
            eventContent={renderEventContent}
            dayHeaderContent={dayHeaderContent}
            select={handleSelectRange}
            eventDrop={handleDropEvent}
            eventClick={handleSelectEvent}
            eventResize={handleResizeEvent}
            height={isMobile || isDesktopSmallScreen ? 'auto' : pageHeight}
            selectAllow={selectAllow}
            timeZone={getTimezone()}
            eventTimeFormat={{
              hour: 'numeric',
              minute: '2-digit',
              meridiem: 'short', // This will display AM/PM
            }}
            plugins={[
              listPlugin,
              dayGridPlugin,
              timelinePlugin,
              timeGridPlugin,
              interactionPlugin,
              momentTimezonePlugin,
            ]}
            eventDidMount={handleEventDidMount}
            datesSet={handleDatesSet}
            noEventsContent={<CustomNoRowsOverlay text={t('looksLikeTheresNoDataForTheSelectedMonth', { ns: 'common' })} />}
          />
        </CalendarStyled>
        <ShiftPopover anchorEl={anchorEl} setAnchorEl={setAnchorEl} />
      </div>
    </Card>
  );
};

export {
  Calendar,
};
